import type { ViewConfigTypeEnum } from '@salescore/client-api'
import { policyUtil, type ViewPolicyActionUtil } from '@salescore/features'

import { can } from './useCan'
import { type Me, useMe } from './useMe'

export function useCanForView(
  action: ViewPolicyActionUtil,
  view: { id?: string; createdById?: string | null; type: ViewConfigTypeEnum },
): boolean {
  const me = useMe()
  return canForView(me, action, view)
}

export const canForView = (
  me: Me,
  action: ViewPolicyActionUtil,
  view: { id?: string; createdById?: string | null; type: ViewConfigTypeEnum },
): boolean => {
  const subject = view.id === undefined ? undefined : policyUtil.toSubject({ ...view, id: view.id }, 'view')
  const canViewDisplayConfigEdit = can(me, 'view-display-config-edit', subject)
  const canSheetCreate = can(me, 'sheet-create', subject)
  const canSheetUpdate = can(me, 'sheet-update', subject)
  const canSheetDelete = can(me, 'sheet-delete', subject)
  const canSheetCreateNewRecord = can(me, 'sheet-create-new-record', subject)
  const canSheetSaveRecord = can(me, 'sheet-save-record', subject)
  const canSheetDeleteRecord = can(me, 'sheet-delete-record', subject)
  const canSheetOpenRelationInputForm = can(me, 'sheet-open-relation-input-form', subject)
  const canSheetDownloadCsv = can(me, 'sheet-download-csv', subject)

  const canKpiCreate = can(me, 'kpi-create', subject)
  const canKpiUpdate = can(me, 'kpi-update', subject)
  const canKpiDelete = can(me, 'kpi-delete', subject)
  const canKpiCreateNewRecord = can(me, 'kpi-create-new-record', subject)
  const canKpiSaveRecord = can(me, 'kpi-save-record', subject)
  const canKpiDeleteRecord = can(me, 'kpi-delete-record', subject)
  const canKpiOpenRelationInputForm = can(me, 'kpi-open-relation-input-form', subject)
  const canKpiDownloadCsv = can(me, 'kpi-download-csv', subject)

  const canKpiPivotCreate = can(me, 'kpi-pivot-create', subject)
  const canKpiPivotUpdate = can(me, 'kpi-pivot-update', subject)
  const canKpiPivotDelete = can(me, 'kpi-pivot-delete', subject)
  const canKpiPivotCreateNewRecord = true
  const canKpiPivotSaveRecord = true
  const canKpiPivotDeleteRecord = true
  const canKpiPivotOpenRelationInputForm = true
  const canKpiPivotDownloadCsv = can(me, 'kpi-pivot-download-csv', subject)

  switch (view.type) {
    case 'sheet': {
      switch (action) {
        case 'create': {
          return canSheetCreate
        }
        case 'update': {
          return canSheetUpdate
        }
        case 'display-config-edit': {
          return canViewDisplayConfigEdit
        }
        case 'delete': {
          return canSheetDelete
        }
        case 'create-new-record': {
          return canSheetCreateNewRecord
        }
        case 'save-record': {
          return canSheetSaveRecord
        }
        case 'delete-record': {
          return canSheetDeleteRecord
        }
        case 'open-relation-input-form': {
          return canSheetOpenRelationInputForm
        }
        case 'download-csv': {
          return canSheetDownloadCsv
        }
      }
      break
    }
    case 'kpi': {
      switch (action) {
        case 'create': {
          return canKpiCreate
        }
        case 'update': {
          return canKpiUpdate
        }
        case 'display-config-edit': {
          return canViewDisplayConfigEdit
        }
        case 'delete': {
          return canKpiDelete
        }
        case 'create-new-record': {
          return canKpiCreateNewRecord
        }
        case 'save-record': {
          return canKpiSaveRecord
        }
        case 'delete-record': {
          return canKpiDeleteRecord
        }
        case 'open-relation-input-form': {
          return canKpiOpenRelationInputForm
        }
        case 'download-csv': {
          return canKpiDownloadCsv
        }
      }
      break
    }
    case 'kpiPivot': {
      switch (action) {
        case 'create': {
          return canKpiPivotCreate
        }
        case 'update': {
          return canKpiPivotUpdate
        }
        case 'display-config-edit': {
          return canViewDisplayConfigEdit
        }
        case 'delete': {
          return canKpiPivotDelete
        }
        case 'create-new-record': {
          return canKpiPivotCreateNewRecord
        }
        case 'save-record': {
          return canKpiPivotSaveRecord
        }
        case 'delete-record': {
          return canKpiPivotDeleteRecord
        }
        case 'open-relation-input-form': {
          return canKpiPivotOpenRelationInputForm
        }
        case 'download-csv': {
          return canKpiPivotDownloadCsv
        }
      }
      break
    }
    case 'form': {
      return true
    }
  }
}
